// State + effects behind MainLayout / MainLayoutV2, ported from
// MainLayout.res. All dependencies are TypeScript now (ModalManager,
// SelectedCityHook, MainLayoutHelper, CookieUtils, CommonHelper, and
// AnalyticsHelper.getCategoryName — the full route-table port, not the
// partial CategoryNameHelper).
import { getCookie } from '@porterin/booking-flow/src/order-review/view/helpers/CookieHelper';
import { COOKIE_KEYS } from '@porterin/booking-flow/src/order-review/view/stores/OrderReviewStore';
import { getUserCity } from '@porterin/utils/src/helpers/LocalStorage';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';

import {
  fetchOrderHistory,
  fetchProfileV2,
} from '../../containers/fare-estimate-v1/domain/helpers/MainLayoutHelper';
import {
  AgreedDocs,
  AuthContextType,
  AuthUser,
} from '../../containers/login/view-model/contexts/auth-context/AuthContextV1';
import { CityContextType } from '../../contexts/city-context/CityContext';
import { ModalContextType } from '../../contexts/modal-context/ModalContext';
import { useModal } from '../../contexts/modal-context/ModalManager';
import { getCategoryName } from '../../shared/core/helpers/AnalyticsHelper';
import { persistQueryParams } from '../../shared/core/helpers/CommonHelper';
import { useCitySelected } from '../../shared/core/hooks/SelectedCityHook';

// ReScript `Catalyst.Loadable` runtime representation (see compiled
// MainLayout.mjs): `Init` = 0, `Live(x)` = { TAG: 1, _0: x }. `fetchProfileV2`
// / `fetchOrderHistory` set the Loading/Live/Error states internally, so the
// layout only ever produces `Init` and `Live(false)`. Kept in the ReScript
// shape because downstream ReScript consumers of the auth context still read
// these fields as ReScript variants.
const RESCRIPT_LOADABLE_INIT = 0;
const rescriptLoadableLive = (
  value: unknown,
): { TAG: number; _0: unknown } => ({
  TAG: 1,
  _0: value,
});

export interface MainLayoutBindings {
  authContextValue: AuthContextType;
  cityContextValue: CityContextType;
  modalContextValue: ModalContextType;
  modalComponent: ReactNode;
  layoutKey: string;
}

export interface UseMainLayoutParams {
  // MainLayout persists query params for GA attribution; MainLayoutV2 does not
  // (parity with the `.res` files — V2 hardcodes `query_params: ""`).
  persistQuery: boolean;
}

export function useMainLayout({
  persistQuery,
}: UseMainLayoutParams): MainLayoutBindings {
  const [modalComponent, modalState, toggleModal] = useModal();
  const [citySelected, setCitySelected] = useCitySelected<string>();
  const [citySelectedLabel, setCitySelectedLabel] = useCitySelected<string>();
  const router = useRouter();
  const authToken = getCookie(COOKIE_KEYS.AUTH_TOKEN) ?? '';
  const mobile = getCookie(COOKIE_KEYS.MOBILE) ?? '';
  const [user, setUser] = useState<AuthUser | undefined>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticatedLoadable, setIsAuthenticatedLoadable] =
    useState<unknown>(RESCRIPT_LOADABLE_INIT);
  const [orderHistory, setOrderHistory] = useState<unknown>(
    RESCRIPT_LOADABLE_INIT,
  );
  const [agreedDocs, setAgreedDocs] = useState<AgreedDocs | undefined>();

  useEffect(() => {
    const city = getUserCity();
    if (city) {
      setCitySelected(city);
      setCitySelectedLabel(city);
    }
  }, []);

  useEffect(() => {
    const hasAuthCookies = authToken.length > 0 && mobile.length > 0;
    if (hasAuthCookies) {
      fetchProfileV2(
        setUser,
        user,
        setIsAuthenticated,
        setIsAuthenticatedLoadable,
        () => fetchOrderHistory(setOrderHistory),
      );
    } else {
      setIsAuthenticated(false);
      setIsAuthenticatedLoadable(rescriptLoadableLive(false));
    }
    // Parity with MainLayout.res: the effect only re-subscribes to
    // `is_authenticated`. Widening deps would change the fetch cadence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const authContextValue = {
    is_authenticated: isAuthenticated,
    set_is_authenticated: (value: boolean): void => setIsAuthenticated(value),
    is_authenticated_loadable: isAuthenticatedLoadable,
    set_is_authenticated_loadable: (value: unknown): void =>
      setIsAuthenticatedLoadable(value),
    order_history: orderHistory,
    set_order_history: (value: unknown): void => setOrderHistory(value),
    user,
    set_user: (value: AuthUser | undefined): void => setUser(value),
    agreed_docs: agreedDocs,
    set_agreed_docs: (value: AgreedDocs | undefined): void =>
      setAgreedDocs(value),
    // The loadable fields hold ReScript variants at runtime (see above); the
    // AuthContextType models them with the TS `Loadable` shape. Consumers only
    // read `is_authenticated`/`user`, which share a runtime shape. Same cast as
    // AuthContextV1.tsx.
  } as unknown as AuthContextType;

  const cityContextValue: CityContextType = {
    city_selected: citySelected,
    selected_city_label: citySelectedLabel,
    set_city_selected: (city, label): void => {
      setCitySelected(city);
      setCitySelectedLabel(label);
    },
    category: getCategoryName(router),
    query_params: persistQuery ? persistQueryParams(router.asPath, router) : '',
  };

  const modalContextValue: ModalContextType = {
    modal: modalState,
    toggleModal: (modal): void => toggleModal(() => modal),
  };

  const layoutKey = `${router.locale}${isAuthenticated}`;

  return {
    authContextValue,
    cityContextValue,
    modalContextValue,
    modalComponent,
    layoutKey,
  };
}
